import { useEffect, useRef, useState } from 'react'
import { RetentionPanel } from './RetentionPanel'
import { AiSettingsPanel } from './AiSettingsPanel'
import { fetchSettings, upsertSetting, type SystemSetting } from '../api/settings'
import {
  Page,
  PageHeader,
  Button,
  Callout,
  LoadingState,
  Card,
  CardHeader,
  CardContent,
  DataTable,
  StatusPill,
  TextField,
  Icon,
  PaneLayout,
  FormGrid,
  FormSection,
} from '../components/ui'

interface FormFields {
  company_name: string
  company_email: string
  company_phone: string
  vat_number: string
  pec: string
  address: string
  smtp_host: string
  smtp_port: string
  smtp_username: string
  smtp_password: string
  smtp_from_email: string
  smtp_from_name: string
  invoice_prefix: string
  default_vat: string
  payment_days: string
}

const EMPTY_FORM: FormFields = {
  company_name: '',
  company_email: '',
  company_phone: '',
  vat_number: '',
  pec: '',
  address: '',
  smtp_host: '',
  smtp_port: '',
  smtp_username: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: '',
  invoice_prefix: '',
  default_vat: '',
  payment_days: '',
}

function settingsToForm(raw: SystemSetting[]): FormFields {
  const map: Record<string, string> = {}
  raw.forEach(s => { map[s.key] = s.value ?? '' })
  return {
    company_name:   map['company_name']   ?? '',
    company_email:  map['company_email']  ?? '',
    company_phone:  map['company_phone']  ?? '',
    vat_number:     map['vat_number']     ?? '',
    pec:            map['pec']            ?? '',
    address:        map['address']        ?? '',
    smtp_host:      map['smtp_host']      ?? '',
    smtp_port:      map['smtp_port']      ?? '',
    smtp_username:  map['smtp_username']  ?? '',
    smtp_password:  map['smtp_password']  ?? '',
    smtp_from_email: map['smtp_from_email'] ?? '',
    smtp_from_name:  map['smtp_from_name']  ?? '',
    invoice_prefix: map['invoice_prefix'] ?? '',
    default_vat:    map['default_vat']    ?? '',
    payment_days:   map['payment_days']   ?? '',
  }
}

export function SettingsPage() {
  const [formData, setFormData] = useState<FormFields>(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchSettings()
      .then(data => setFormData(settingsToForm(data)))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    return () => {
      if (successTimer.current !== null) clearTimeout(successTimer.current)
    }
  }, [])

  function handleChange(field: keyof FormFields) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData(prev => ({ ...prev, [field]: e.target.value }))
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (saving) return
    if (successTimer.current !== null) clearTimeout(successTimer.current)
    setSuccessMsg(null)
    setError(null)
    setSaving(true)

    // All 15 fields map to independent setting keys with no cross-field
    // validation or ordering dependency, so they're saved in parallel
    // (Promise.all) rather than sequentially — a save is 15 independent
    // PUT calls, not a transaction, and there's no reason to pay 15x the
    // round-trip latency serializing them. If any call fails, Promise.all
    // rejects and the catch below shows an honest error instead of the
    // success message — some fields may have already persisted on the
    // server in that case, but the UI never claims success it didn't verify.
    const entries = Object.entries(formData) as [keyof FormFields, string][]
    Promise.all(entries.map(([key, value]) => upsertSetting(key, value)))
      .then(() => {
        setSuccessMsg('Impostazioni salvate con successo')
        successTimer.current = setTimeout(() => setSuccessMsg(null), 3000)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Errore nel salvataggio delle impostazioni')
      })
      .finally(() => setSaving(false))
  }

  return (
    <Page>
      <PageHeader title="Impostazioni generali" icon="settings" />

      {error && <Callout tone="error">{error}</Callout>}
      {successMsg && <Callout tone="success">{successMsg}</Callout>}

      {loading ? (
        <LoadingState />
      ) : (
        <PaneLayout
          variant="supporting"
          main={
          <div className="d-flex flex-column gap-4">
            {/* Logo card */}
            <Card variant="outlined">
              <CardHeader title="Logo aziendale" icon="image" />
              <CardContent>
                <FormGrid>
                  <Card as="div" variant="filled" padding="md" className="text-center text-muted">
                    <Icon name="business" size={40} className="d-block mb-1" />
                    <span className="md-typescale-body-small">Nessun logo</span>
                  </Card>
                  <div className="d-flex flex-column gap-3 align-items-start">
                    <TextField
                      type="file"
                      accept="image/*"
                      id="logoUpload"
                      label="Logo aziendale"
                    />
                    <Button variant="tonal" size="sm" icon="upload">
                      Carica logo
                    </Button>
                  </div>
                </FormGrid>
              </CardContent>
            </Card>

            {/* Company data form */}
            <Card variant="outlined">
              <CardHeader title="Dati aziendali" icon="business" />
              <CardContent>
                <form onSubmit={handleSubmit} className="d-flex flex-column gap-4">
                  {/* Company base info */}
                  <FormSection title="Anagrafica" columns={3}>
                    <TextField label="Nome Azienda" type="text" value={formData.company_name} onChange={handleChange('company_name')} />
                    <TextField label="Email Azienda" type="email" value={formData.company_email} onChange={handleChange('company_email')} />
                    <TextField label="Telefono" type="text" value={formData.company_phone} onChange={handleChange('company_phone')} />
                    <TextField label="P.IVA" type="text" value={formData.vat_number} onChange={handleChange('vat_number')} />
                    <TextField label="PEC" type="email" value={formData.pec} onChange={handleChange('pec')} />
                    <TextField className="md-span-full" label="Indirizzo" multiline rows={2} value={formData.address} onChange={handleChange('address')} />
                  </FormSection>

                  {/* SMTP */}
                  <FormSection title="Email (SMTP)" columns={3}>
                    <TextField label="Host SMTP" type="text" value={formData.smtp_host} onChange={handleChange('smtp_host')} />
                    <TextField label="Porta" type="text" value={formData.smtp_port} onChange={handleChange('smtp_port')} />
                    <TextField label="Username" type="text" value={formData.smtp_username} onChange={handleChange('smtp_username')} />
                    <TextField label="Password" type="password" value={formData.smtp_password} onChange={handleChange('smtp_password')} />
                    <TextField label="Email Mittente" type="email" value={formData.smtp_from_email} onChange={handleChange('smtp_from_email')} />
                    <TextField label="Nome Mittente" type="text" value={formData.smtp_from_name} onChange={handleChange('smtp_from_name')} />
                  </FormSection>

                  {/* Invoicing */}
                  <FormSection title="Fatturazione" columns={3}>
                    <TextField label="Prefisso Fattura" type="text" value={formData.invoice_prefix} onChange={handleChange('invoice_prefix')} />
                    <TextField label="IVA Default %" type="text" value={formData.default_vat} onChange={handleChange('default_vat')} />
                    <TextField label="Giorni Scadenza" type="text" value={formData.payment_days} onChange={handleChange('payment_days')} />
                  </FormSection>

                  <div>
                    <Button type="submit" variant="filled" icon={saving ? undefined : 'save'} loading={saving} disabled={saving}>
                      {saving ? 'Salvataggio…' : 'Salva Impostazioni'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
          }
          pane={
          <div className="d-flex flex-column gap-3">
            {/* Gestione Utenti */}
            <Card variant="outlined">
              <CardContent>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Icon name="people" size={24} className="text-primary" />
                  <strong>Gestione Utenti</strong>
                </div>
                <p className="text-muted md-typescale-body-small mb-3">Gestisci gli utenti del sistema e i loro permessi.</p>
                <Button variant="outlined" size="sm">Gestisci utenti</Button>
              </CardContent>
            </Card>

            {/* Categorie Utenti */}
            <Card variant="outlined">
              <CardContent>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Icon name="category" size={24} className="text-primary" />
                  <strong>Categorie Utenti</strong>
                </div>
                <p className="text-muted md-typescale-body-small mb-3">Configura le categorie e i ruoli degli utenti.</p>
                <Button variant="outlined" size="sm">Gestisci categorie</Button>
              </CardContent>
            </Card>

            {/* Audit Log */}
            <Card variant="outlined">
              <CardContent>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Icon name="history" size={24} className="text-primary" />
                  <strong>Audit Log</strong>
                </div>
                <p className="text-muted md-typescale-body-small mb-3">Visualizza il registro delle attività del sistema.</p>
                <Button variant="outlined" size="sm">Visualizza log</Button>
              </CardContent>
            </Card>

            <RetentionPanel />

            <AiSettingsPanel />

            {/* Info Sistema */}
            <Card variant="outlined" padding="none">
              <CardHeader title="Info sistema" icon="info" />
              <DataTable variant="plain">
                <tbody>
                  <tr>
                    <td className="text-muted md-typescale-body-small">Versione CRM</td>
                    <td><StatusPill tone="primary">1.0.0</StatusPill></td>
                  </tr>
                  <tr>
                    <td className="text-muted md-typescale-body-small">Backend</td>
                    <td className="md-typescale-body-small">.NET 10</td>
                  </tr>
                  <tr>
                    <td className="text-muted md-typescale-body-small">Database</td>
                    <td className="md-typescale-body-small">PostgreSQL</td>
                  </tr>
                  <tr>
                    <td className="text-muted md-typescale-body-small">Frontend</td>
                    <td className="md-typescale-body-small">React 18</td>
                  </tr>
                </tbody>
              </DataTable>
            </Card>
          </div>
          }
        />
      )}
    </Page>
  )
}
